/**
 * SimulationEngine - Generic, data-driven biochemical simulation engine.
 *
 * Uses Michaelis-Menten kinetics: rate = Vmax * [S] / (Km + [S])
 * Competing reactions for the same substrate are processed simultaneously,
 * with substrate distributed proportionally to each reaction's rate.
 *
 * No hardcoded pathway logic — all behavior comes from scenario definitions.
 */

export class SimulationEngine {
  constructor(scenario) {
    this.scenario = scenario;
    this.state = this.initializeState(scenario);
    this.tickCount = 0;
    this.totalDoseAdministered = 0;
    this.feedbackLog = [];
    this.isComplete = false;
    this.outcome = null; // 'win' | 'lose' | null
  }

  initializeState(scenario) {
    const molecules = {};
    for (const mol of scenario.molecules) {
      molecules[mol.id] = {
        ...mol,
        amount: mol.initialAmount || 0,
      };
    }

    const enzymes = {};
    for (const enz of scenario.enzymes) {
      enzymes[enz.id] = { ...enz };
    }

    const damage = {};
    for (const dmg of scenario.damageTrackers) {
      damage[dmg.id] = {
        ...dmg,
        value: dmg.initialValue,
      };
    }

    return {
      molecules,
      enzymes,
      damage,
      interventions: {},
      modifiers: { ...scenario.defaultModifiers },
    };
  }

  /** Apply a dose of a molecule */
  addDose(moleculeId, amount) {
    if (this.state.molecules[moleculeId]) {
      this.state.molecules[moleculeId].amount += amount;
      if (moleculeId === 'APAP') {
        this.totalDoseAdministered += amount;
      }
    }
  }

  /** Toggle a scenario modifier (e.g., alcohol exposure) */
  setModifier(key, value) {
    this.state.modifiers[key] = value;
    const modifier = this.scenario.modifiers.find(m => m.id === key);
    if (modifier && modifier.effects) {
      for (const effect of modifier.effects) {
        if (value) {
          this.applyModifierEffect(effect);
        } else {
          this.revertModifierEffect(effect);
        }
      }
    }
  }

  applyModifierEffect(effect) {
    if (effect.target === 'enzyme' && this.state.enzymes[effect.enzymeId]) {
      this.state.enzymes[effect.enzymeId].activityMultiplier =
        (this.state.enzymes[effect.enzymeId].activityMultiplier || 1) * effect.multiplier;
    }
    if (effect.target === 'molecule' && this.state.molecules[effect.moleculeId]) {
      this.state.molecules[effect.moleculeId].amount *= effect.multiplier;
    }
    if (effect.target === 'regeneration' && this.state.molecules[effect.moleculeId]) {
      this.state.molecules[effect.moleculeId].regenRate =
        (this.state.molecules[effect.moleculeId].regenRate || 0) * effect.multiplier;
    }
  }

  revertModifierEffect(effect) {
    if (effect.target === 'enzyme' && this.state.enzymes[effect.enzymeId]) {
      this.state.enzymes[effect.enzymeId].activityMultiplier =
        (this.state.enzymes[effect.enzymeId].activityMultiplier || 1) / effect.multiplier;
    }
    if (effect.target === 'regeneration' && this.state.molecules[effect.moleculeId]) {
      this.state.molecules[effect.moleculeId].regenRate =
        (this.state.molecules[effect.moleculeId].regenRate || 0) / effect.multiplier;
    }
  }

  /** Activate an intervention (e.g., NAC) */
  activateIntervention(interventionId) {
    const intervention = this.scenario.interventions.find(i => i.id === interventionId);
    if (!intervention) return;

    this.state.interventions[interventionId] = {
      active: true,
      tickActivated: this.tickCount,
      ...intervention,
    };

    this.addFeedback(intervention.activationMessage, 'intervention');
  }

  /** Main simulation tick */
  tick() {
    if (this.isComplete) return this.state;

    this.tickCount++;

    // 1. Apply intervention effects
    this.processInterventions();

    // 2. Process all reactions using Michaelis-Menten kinetics
    //    Competing reactions for the same substrate are processed simultaneously
    const feedback = this.processAllReactions();

    // 3. Process damage rules (delayed effects)
    this.processDamageRules();

    // 4. Check win/lose conditions
    this.checkEndConditions();

    // 5. Apply regeneration AFTER reactions so mid-tick dips are visible
    this.processRegeneration();

    // 6. Log feedback
    for (const fb of feedback) {
      this.addFeedback(fb.message, fb.type);
    }

    return this.getState();
  }

  processRegeneration() {
    for (const mol of Object.values(this.state.molecules)) {
      if (mol.regenRate && mol.regenRate > 0 && mol.amount < (mol.maxAmount || Infinity)) {
        const regenAmount = Math.min(
          mol.regenRate,
          (mol.maxAmount || Infinity) - mol.amount
        );
        mol.amount += regenAmount;
      }
    }
  }

  processInterventions() {
    for (const [, intervention] of Object.entries(this.state.interventions)) {
      if (!intervention.active) continue;

      for (const effect of intervention.effects) {
        if (effect.type === 'boost_regeneration') {
          const mol = this.state.molecules[effect.moleculeId];
          if (mol) {
            const healthTracker = this.state.damage['hepatocyte_health'];
            const effectiveness = healthTracker
              ? Math.max(0.2, healthTracker.value / 100)
              : 1;
            const boost = effect.amount * effectiveness;
            mol.amount = Math.min(
              (mol.maxAmount || Infinity),
              mol.amount + boost
            );
          }
        }
        if (effect.type === 'direct_add') {
          const mol = this.state.molecules[effect.moleculeId];
          if (mol) {
            mol.amount = Math.min(
              (mol.maxAmount || Infinity),
              mol.amount + effect.amount
            );
          }
        }
      }
    }
  }

  /**
   * Process all reactions using Michaelis-Menten kinetics.
   * Groups reactions by substrate and distributes substrate proportionally
   * to each reaction's calculated rate — this models real enzyme competition.
   */
  processAllReactions() {
    const feedback = [];

    // Group reactions by substrate
    const groups = {};
    for (const reaction of this.scenario.reactions) {
      const subId = reaction.substrate;
      if (!groups[subId]) groups[subId] = [];
      groups[subId].push(reaction);
    }

    // Snapshot toxic intermediate amounts BEFORE processing.
    // GSH conjugation can only neutralize NAPQI that existed at tick start,
    // not NAPQI produced this tick. New NAPQI persists until next tick.
    // This models the real kinetic delay and allows NAPQI to accumulate
    // when CYP production exceeds GSH conjugation capacity.
    const toxicCaps = {};
    for (const mol of Object.values(this.state.molecules)) {
      if (mol.category === 'toxic') {
        toxicCaps[mol.id] = mol.amount;
      }
    }

    // Process each substrate group
    for (const [substrateId, reactions] of Object.entries(groups)) {
      const substrate = this.state.molecules[substrateId];
      if (!substrate || substrate.amount <= 0.01) continue;

      // For toxic intermediates, only allow conjugation of pre-existing amount
      // (NAPQI produced this tick persists until next tick)
      const availableS = (toxicCaps[substrateId] !== undefined)
        ? Math.min(substrate.amount, toxicCaps[substrateId])
        : substrate.amount;

      const S = availableS;

      // Calculate Michaelis-Menten rate for each reaction
      const rateInfo = reactions.map(rxn => {
        const enzyme = this.state.enzymes[rxn.enzyme];
        const activityMult = enzyme ? (enzyme.activityMultiplier || 1) : 1;
        const vmax = rxn.vmax * activityMult;
        const km = rxn.km;

        // Michaelis-Menten: rate = Vmax * [S] / (Km + [S])
        let rate = vmax * S / (km + S);

        // Check cofactor availability — limits the effective rate
        let cofactorLimited = false;
        let cofactorFraction = 1.0;
        if (rxn.cofactor) {
          const cofactor = this.state.molecules[rxn.cofactor];
          if (!cofactor || cofactor.amount <= 0) {
            cofactorLimited = true;
            rate = 0;
          } else {
            // Rate scales with cofactor availability below a threshold
            const cofactorRatio = cofactor.amount / (cofactor.maxAmount || 100);
            if (cofactorRatio < 0.3) {
              cofactorFraction = cofactorRatio / 0.3;
              rate *= cofactorFraction;
            }
          }
        }

        return { rxn, rate, cofactorLimited, cofactorFraction };
      });

      // Total rate demanded from this substrate
      const totalRate = rateInfo.reduce((sum, r) => sum + r.rate, 0);
      if (totalRate <= 0) continue;

      // If total demand exceeds supply, scale proportionally
      const scaleFactor = totalRate > S ? S / totalRate : 1.0;

      // Execute each reaction with its allocated amount
      for (const { rxn, rate, cofactorLimited, cofactorFraction } of rateInfo) {
        const allocated = rate * scaleFactor;
        if (allocated <= 0) continue;

        // Consume substrate
        substrate.amount -= allocated;

        // Produce product
        if (rxn.product) {
          const product = this.state.molecules[rxn.product];
          if (product) {
            product.amount += allocated * (rxn.productRatio || 1);
          }
        }

        // Consume cofactor
        if (rxn.cofactor && !cofactorLimited) {
          const cofactor = this.state.molecules[rxn.cofactor];
          if (cofactor) {
            cofactor.amount = Math.max(0, cofactor.amount - allocated * (rxn.cofactorCost || 1));
          }
        }

        // Generate feedback
        if (cofactorLimited && S > 1) {
          feedback.push({
            message: rxn.cofactorDepletionMessage || `${rxn.cofactor} depleted — ${rxn.name} blocked.`,
            type: 'warning',
          });
        } else if (cofactorFraction < 1.0 && S > 1) {
          feedback.push({
            message: rxn.cofactorLowMessage || `${rxn.cofactor} running low — ${rxn.name} slowing.`,
            type: 'warning',
          });
        }

        if (rxn.toxic && allocated > 0.3 && rxn.shuntMessage) {
          // Only show shunt message when CYP is processing a significant fraction
          const fraction = allocated / (totalRate * scaleFactor);
          if (fraction > 0.15) {
            feedback.push({
              message: rxn.shuntMessage,
              type: 'danger',
            });
          }
        }
      }
    }

    return feedback;
  }

  processDamageRules() {
    for (const rule of this.scenario.damageRules) {
      const source = this.state.molecules[rule.source] || this.state.damage[rule.source];
      if (!source) continue;

      const sourceValue = source.amount !== undefined ? source.amount : source.value;
      const target = this.state.damage[rule.target];
      if (!target) continue;

      if (sourceValue > (rule.threshold || 0)) {
        const excess = sourceValue - (rule.threshold || 0);
        const damageAmount = excess * rule.rate;

        if (rule.direction === 'decrease') {
          target.value = Math.max(target.min || 0, target.value - damageAmount);
        } else {
          target.value = Math.min(target.max || 100, target.value + damageAmount);
        }

        if (damageAmount > 0.05 && rule.feedbackMessage) {
          this.addFeedback(rule.feedbackMessage, 'danger');
        }
      }

      // Natural recovery when source is low
      if (rule.recoveryRate && sourceValue <= (rule.threshold || 0)) {
        if (rule.direction === 'decrease') {
          target.value = Math.min(
            target.initialValue || 100,
            target.value + rule.recoveryRate
          );
        } else {
          target.value = Math.max(
            target.initialValue || 0,
            target.value - rule.recoveryRate
          );
        }
      }
    }
  }

  checkEndConditions() {
    // Check lose conditions
    for (const condition of this.scenario.loseConditions) {
      const tracker = this.state.damage[condition.trackerId];
      if (!tracker) continue;

      if (condition.type === 'below' && tracker.value <= condition.threshold) {
        this.isComplete = true;
        this.outcome = 'lose';
        this.addFeedback(condition.message, 'critical');
        return;
      }
      if (condition.type === 'above' && tracker.value >= condition.threshold) {
        this.isComplete = true;
        this.outcome = 'lose';
        this.addFeedback(condition.message, 'critical');
        return;
      }
    }

    // Check win conditions (all must be met)
    const winMet = this.scenario.winConditions.every(condition => {
      if (condition.type === 'molecule_below') {
        const mol = this.state.molecules[condition.moleculeId];
        return mol && mol.amount <= condition.threshold;
      }
      if (condition.type === 'damage_above') {
        const tracker = this.state.damage[condition.trackerId];
        return tracker && tracker.value >= condition.threshold;
      }
      return false;
    });

    if (winMet && this.tickCount > 5) {
      this.isComplete = true;
      this.outcome = 'win';
      this.addFeedback('The hepatocyte has survived! All toxins cleared.', 'success');
    }
  }

  addFeedback(message, type = 'info') {
    const last = this.feedbackLog[this.feedbackLog.length - 1];
    if (last && last.message === message && this.tickCount - last.tick < 3) return;

    this.feedbackLog.push({
      tick: this.tickCount,
      message,
      type,
      timestamp: Date.now(),
    });

    if (this.feedbackLog.length > 100) {
      this.feedbackLog = this.feedbackLog.slice(-50);
    }
  }

  getState() {
    return {
      tickCount: this.tickCount,
      molecules: { ...this.state.molecules },
      enzymes: { ...this.state.enzymes },
      damage: { ...this.state.damage },
      modifiers: { ...this.state.modifiers },
      interventions: { ...this.state.interventions },
      feedbackLog: this.feedbackLog,
      isComplete: this.isComplete,
      outcome: this.outcome,
    };
  }

  /** Generate end-of-game summary */
  generateSummary(externalTotalDose) {
    const state = this.getState();
    const gshMax = this.scenario.molecules.find(m => m.id === 'GSH')?.maxAmount || 100;
    const gshPct = ((state.molecules.GSH?.amount || 0) / gshMax) * 100;

    const totalDose = externalTotalDose || this.totalDoseAdministered || 1;
    const apapRemaining = ((state.molecules.APAP?.amount || 0) / totalDose * 100);
    const glucuronidePct = ((state.molecules.APAP_glucuronide?.amount || 0) / totalDose * 100);
    const sulfatePct = ((state.molecules.APAP_sulfate?.amount || 0) / totalDose * 100);
    const gshConjugatePct = ((state.molecules.APAP_GSH?.amount || 0) / totalDose * 100);
    const napqiAmount = state.molecules.NAPQI?.amount || 0;

    const summary = {
      outcome: this.outcome,
      ticksSurvived: this.tickCount,
      finalHealth: state.damage.hepatocyte_health?.value || 0,
      finalMitoIntegrity: state.damage.mitochondrial_integrity?.value || 0,
      peakROS: state.damage.ros_level?.value || 0,
      gshRemaining: gshPct,
      gshDepleted: gshPct < 30,
      nacUsed: !!state.interventions.nac?.active,
      nacTiming: state.interventions.nac?.tickActivated || null,
      apapRemaining: apapRemaining,
      glucuronidePct: glucuronidePct,
      sulfatePct: sulfatePct,
      gshConjugatePct: gshConjugatePct,
      napqiAmount: napqiAmount,
      factors: [],
    };

    // Determine contributing factors
    if (state.modifiers.alcohol) {
      summary.factors.push('Alcohol exposure induced CYP2E1 (2-5x) and depleted GSH by 20-40%, increasing NAPQI production while reducing detox capacity.');
    }
    if (state.modifiers.lowGSH) {
      summary.factors.push('Low baseline glutathione (fasting/malnutrition) reduced the GSH pool, lowering the threshold for NAPQI toxicity.');
    }
    if (summary.gshDepleted) {
      summary.factors.push('Glutathione fell below 30% of normal — the critical threshold (Mitchell 1973) where NAPQI begins binding to hepatocyte proteins.');
    }
    if (summary.nacUsed && summary.nacTiming > 16) {
      summary.factors.push('NAC was administered late. Clinical data shows <8h = near 100% protection; 16-24h = only 50-60% protection (Smilkstein 1988).');
    } else if (summary.nacUsed && summary.nacTiming <= 16) {
      summary.factors.push('Early NAC administration restored cysteine supply for GSH synthesis, maintaining detoxification capacity.');
    }
    if (state.modifiers.cyp2e1Fast) {
      summary.factors.push('CYP2E1 fast metabolizer genotype (e.g., *5B c2 variant) increased NAPQI production. CYP2E1 accounts for ~74% of NAPQI formation in vivo (Manyike 2000).');
    }
    if (state.modifiers.cyp2e1Slow) {
      summary.factors.push('CYP2E1 slow metabolizer genotype reduced NAPQI production, providing a protective effect.');
    }
    if ((state.molecules.PAPS?.amount || 0) < 1) {
      summary.factors.push('PAPS was depleted — sulfation (normally ~30% of clearance) shut down, shunting more APAP through CYP enzymes to NAPQI.');
    }

    return summary;
  }

  /** Reset the simulation */
  reset() {
    this.state = this.initializeState(this.scenario);
    this.tickCount = 0;
    this.totalDoseAdministered = 0;
    this.feedbackLog = [];
    this.isComplete = false;
    this.outcome = null;
  }
}
